// src/components/ui/calendar/navigation/CalendarTimeRangeToggle/CalendarTimeRangeToggleShadcn.jsx
import React, { useState } from 'react';
import { Clock, ChevronDown } from 'lucide-react';

// shadcn components
import { Button } from '../../../button';
import { Popover, PopoverTrigger, PopoverContent } from '../../../popover';

import { cn } from '../../../../../utils/cn';

const CalendarTimeRangeToggleShadcn = ({
  currentRange,
  onRangeChange,
  compact = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Preset time range options
  const rangeOptions = [
    {
      label: 'Business Hours',
      value: { start: 8, end: 18 },
      description: '8 AM - 6 PM'
    },
    {
      label: 'Extended Hours',
      value: { start: 6, end: 22 },
      description: '6 AM - 10 PM'
    },
    {
      label: 'Early Morning',
      value: { start: 5, end: 13 },
      description: '5 AM - 1 PM'
    },
    {
      label: 'Full Day',
      value: { start: 0, end: 24 },
      description: '12 AM - 12 AM'
    }
  ];

  // Find current option label
  const getCurrentLabel = () => {
    const current = rangeOptions.find(
      opt => opt.value.start === currentRange.start && opt.value.end === currentRange.end
    );
    return current ? current.label : 'Custom';
  };

  const getCurrentDescription = () => {
    const current = rangeOptions.find(
      opt => opt.value.start === currentRange.start && opt.value.end === currentRange.end
    );
    return current ? current.description : `${formatHour(currentRange.start)} - ${formatHour(currentRange.end)}`;
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 24) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const handleSelectRange = (range) => {
    onRangeChange(range);
    setIsOpen(false);
  };

  if (compact) {
    // Compact mode: Icon-only button with popover
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "p-1.5 rounded transition-all duration-200 h-auto",
              "bg-slate-800/30 backdrop-blur-sm border border-slate-600/30",
              isOpen
                ? "bg-blue-500/20 text-blue-300 shadow-lg shadow-blue-500/25"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/30",
              className
            )}
            title={`Time Range: ${getCurrentDescription()}`}
          >
            <Clock className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[240px] p-2 bg-slate-800/95 backdrop-blur-xl border-slate-700/50"
          align="start"
          sideOffset={8}
        >
          <div className="px-3 py-2 text-xs text-slate-400 font-medium">
            Display Hours
          </div>
          <div className="space-y-1">
            {rangeOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleSelectRange(option.value)}
                className={cn(
                  "w-full px-3 py-2 rounded-lg text-left transition-colors duration-200",
                  currentRange.start === option.value.start && currentRange.end === option.value.end
                    ? "bg-blue-500/20 text-blue-300"
                    : "text-slate-300 hover:bg-slate-700/50"
                )}
              >
                <div className="text-sm font-medium">{option.label}</div>
                <div className="text-xs text-slate-400">{option.description}</div>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Full mode: Text button with popover
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200 font-medium h-auto",
            "bg-slate-800/30 backdrop-blur-sm border border-slate-600/30",
            isOpen
              ? "bg-blue-500/20 text-blue-300 shadow-lg shadow-blue-500/25"
              : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/30",
            className
          )}
        >
          <Clock className="w-4 h-4" />
          <span className="text-sm">{getCurrentLabel()}</span>
          <ChevronDown className={cn(
            "w-3 h-3 transition-transform duration-200",
            isOpen && "rotate-180"
          )} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[260px] p-2 bg-slate-800/95 backdrop-blur-xl border-slate-700/50"
        align="start"
        sideOffset={8}
      >
        <div className="px-3 py-2 text-xs text-slate-400 font-medium">
          Display Hours
        </div>
        <div className="space-y-1">
          {rangeOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => handleSelectRange(option.value)}
              className={cn(
                "w-full px-3 py-2.5 rounded-lg text-left transition-colors duration-200",
                currentRange.start === option.value.start && currentRange.end === option.value.end
                  ? "bg-blue-500/20 text-blue-300"
                  : "text-slate-300 hover:bg-slate-700/50"
              )}
            >
              <div className="text-sm font-medium">{option.label}</div>
              <div className="text-xs text-slate-400 mt-0.5">{option.description}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CalendarTimeRangeToggleShadcn;
